/**
 *  Event model object
 *
 *  Why is the Event oject still using character codes like 'L' or 'I' or 'A' to identify credit
 *  or non-credit terms?  Because it wants to identify ALL types of classes, whether they are
 *  credit, Adult Ed, CLR, or Continuing Ed. Since those aren't all defined by the academic level
 *  of a term (most notably, CLR and CE classes are both "CE"), we must still use the code charcter
 *  in the term code.
 */

import moment from 'moment';

export default class Event {
    constructor(mem = {}) {
        this.id = mem.id || 0;
        this.start = mem.start;
        this.end = mem.end;
        this.evttype = mem.evttype || '';
        this.mtgtype = mem.mtgtype || '';
        this.title = mem.title || '';
        this.site = mem.site || '';
        this.bldg = mem.bldg || '';
        this.room = mem.room || '';
        this.roomname = mem.roomname || '';
        this.roomcap = mem.roomcap || 0;
        this.roomcomments = mem.roomcomments || '';
        this.secname = mem.secname || '';
        this.sectitle = mem.sectitle || '';
        this.secterm = mem.secterm || '';
        this.seccap = mem.seccap || 0;
        this.facname = mem.facname || '';
        this.attendeecnt = mem.attendeecnt;
        this.setupduration = mem.setupduration || '';
        this.teardownduration = mem.teardownduration || '';
        return this;
    }

    set start(newdatetime) {
        this._start = moment(newdatetime);
    }

    get start() {
        return this._start;
    }

    set end(newdatetime) {
        this._end = moment(newdatetime);
    }

    get end() {
        return this._end;
    }

    get isAcademic() {
        // Final exams are also considered academic
        return this.evttype === 'CS' || Event.ACADEMIC_MTGTYPES.indexOf(this.mtgtype) >= 0;
    }

    get isMeeting() {
        // For now, anything NOT academic is a meeting
        return !this.isAcademic;
    }

    get sectypecode() {
        // WHAT?! You're using the last char in the term code to identify the type? Yep. See notes at top of file.
        let code = this.secterm.toUpperCase().slice(-1);
        // If couldn't get code in term, try finding the term in the title via final exam coding
        if (code.length == 0) {
            const findterm = Event.FINALEXAM_REGEX.exec(this.title);
            if (findterm) code = findterm[1].toUpperCase().slice(-1);
        }
        // Go thru section types and find the one that has this code. Return its key.
        return Object.keys(Event.SECTIONTYPES).find(key=>Event.SECTIONTYPES[key].codes.includes(code));
    }

    get sectype() {
        return Event.SECTIONTYPES[this.sectypecode];
    }

    get locationShort() {
        if (this.bldg && this.room) return `${this.bldg} ${this.room}`.trim();
        if (this.site=='OTHR') return 'Online';
        return null;
    }

    get location() {
        if (this.bldg && this.room) return `${this.bldg} ${this.room} ${this.roomname ? '(' + this.roomname + ')' : ''}`.trim();
        if (this.site=='OTHR') return 'Online';
        return null;
    }

    get isCurrent() {
        const now = moment();
        return now.isSameOrAfter(this.start) && now.isBefore(this.end);
    }

    get isUpcoming() {
        const now = moment();
        return now.isBefore(this.start);
    }

    get isPast() {
        const now = moment();
        return now.isAfter(this.end);
    }

    static countBySecType(events, sectypecode) {
        return events.filter(event=>event.isAcademic && event.sectypecode == sectypecode).length;
    }

    static countByMeeting(events) {
        return events.filter(event=>event.isMeeting).length;
    }

    static countByAcademic(events) {
        return events.filter(event=>event.isAcademic).length;
    }
}

Event.ACADEMIC_MTGTYPES = [
    'FNL',
    'NIU',
    'NIUF'
];
Event.FINALEXAM_REGEX = /^final exam - (\d{5}\w) /i;
Event.SECTIONTYPES = {
    adulted: {description: 'Adult Ed', codes: ['A']},
    clr: {description: 'CLR', codes: ['R']},
    cce: {description: 'Continuing Ed', codes: ['C']},
    credit: {description: 'Credit', codes: ['L', 'I', 'W']}, // eslint-disable-line array-element-newline
    other: {description: 'Other', codes: ['']}
};
// WHAT! Using L/I/W/A/C-character term codes?!? Yep. Please see explanation in comment at top.